import React, { useState } from "react";
import { Link } from "react-router-dom";
import BackgroundLayer from "../components/BackgroundLayer";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function ForgotPasswordScreen() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [googleAccount, setGoogleAccount] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setGoogleAccount(false);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      if (data?.google_account) {
        setGoogleAccount(true);
        toast.info("This email uses Google sign-in.");
      } else {
        setSent(true);
        toast.success("Reset link sent — check your inbox.");
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      <BackgroundLayer section="auth" />
      <div className="flex-1 flex items-center justify-center px-5">
        <div className="glass-card-strong w-full max-w-md p-7 fade-up" data-testid="forgot-card">
          {googleAccount ? (
            <div className="text-center py-2" data-testid="forgot-google-hint">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                style={{ background: "rgba(66,133,244,0.18)", border: "1px solid rgba(66,133,244,0.45)" }}>
                <svg width="28" height="28" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.7 1.1 7.8 3l5.7-5.7C34.5 6.1 29.5 4 24 4c-7.5 0-14 4.2-17.7 10.7z"/>
                  <path fill="#4CAF50" d="M24 44c5.3 0 10.2-2 13.9-5.3l-6.4-5.4C29.4 35 26.8 36 24 36c-5.3 0-9.7-3.3-11.3-7.9l-6.6 5.1C9.9 39.8 16.4 44 24 44z"/>
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.7l6.4 5.4C40.7 35.8 44 30.3 44 24c0-1.2-.1-2.3-.4-3.5z"/>
                </svg>
              </div>
              <h2 className="text-xl font-bold mb-2">Use Google to sign in</h2>
              <p className="text-soft text-sm mb-5">
                <span className="text-white font-semibold">{email}</span> is linked to a Google account. There&apos;s no password to reset — just sign in with Google.
              </p>
              <Link
                to="/login"
                className="btn-primary inline-flex items-center justify-center px-6"
                data-testid="forgot-google-back-login"
              >
                Back to sign in
              </Link>
            </div>
          ) : !sent ? (
            <>
              <h1 className="text-2xl font-bold mb-2 text-shadow-green">Reset your password</h1>
              <p className="text-soft text-sm mb-6">We&apos;ll email a reset link to your inbox.</p>
              <form onSubmit={submit} className="space-y-4">
                <input
                  type="email"
                  required
                  data-testid="forgot-email-input"
                  className="glass-input"
                  placeholder="you@flora.app"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <button data-testid="forgot-submit" disabled={busy} className="btn-primary w-full">
                  {busy ? "Sending…" : "Send reset link"}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-6" data-testid="forgot-success">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                style={{ background: "rgba(76,175,80,0.18)", boxShadow: "var(--glow-green)" }}>
                <CheckCircle2 size={28} className="text-[#4CAF50]" />
              </div>
              <h2 className="text-xl font-bold mb-2">Check your email</h2>
              <p className="text-soft text-sm">If an account exists, the reset link is on its way.</p>
            </div>
          )}
          <div className="text-center mt-6">
            <Link to="/login" className="text-[#4CAF50] text-sm hover:underline">← Back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
